import { SendOutlined, StopOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import * as React from 'react';
import OWButton from '../../../components/ow_button/OWButton';
import { cn } from '../../../utils/ClassNamesUtils';
import {
	OSCAR_CONTENT_INSET,
	OSCAR_DISCLAIMER_COPY,
	OSCAR_ICON_SIZE,
} from '../../constants/AIAgentConstants';

interface OscarComposerProps {
	value: string;
	onChange: (value: string) => void;
	/** Called with the trimmed draft. Never called with an empty string. */
	onSubmit: (value: string) => void;
	placeholder?: string;
	/**
	 * Blocks sending, not typing. The input stays editable so a person can write
	 * ahead while Oscar connects or reconnects, and a blip does not knock focus
	 * out of the box mid-sentence (OPE-5443); the placeholder says why Send is
	 * off.
	 */
	disabled?: boolean;
	/** Shows the spinner on send and blocks a second submit. */
	sending?: boolean;
	/** Given, the send button becomes a stop button while `sending`. */
	onCancel?: () => void;
	/**
	 * Rendered above the input row, inside the composer's bordered region — this
	 * is where clarifying-question cards live (Figma 15125:54189).
	 */
	prompt?: React.ReactNode;
	className?: string;
}

/**
 * Figma node 14998:125342 — pill-shaped input plus a round send button,
 * separated from the conversation by a grey/4 rule.
 *
 * The disclaimer sits directly under the input row, so it reads as part of the
 * input rather than the conversation.
 *
 * This is a real `<form>` so Enter submits exactly once, and the send button is
 * `OWButton` so a double-click cannot fire two requests (OPE-4836).
 */
export const OscarComposer = ({
	value,
	onChange,
	onSubmit,
	placeholder = 'What can I help you do',
	disabled,
	sending,
	onCancel,
	prompt,
	className,
}: OscarComposerProps) => {
	const trimmed = value.trim();

	// A textarea does not submit its form on Enter, so wire it up explicitly and
	// leave Shift+Enter to insert a newline.
	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key !== 'Enter' || event.shiftKey) {
			return;
		}
		event.preventDefault();
		if (!trimmed || disabled || sending) {
			return;
		}
		onSubmit(trimmed);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!trimmed || disabled || sending) {
			return;
		}
		onSubmit(trimmed);
	};

	return (
		<form
			onSubmit={handleSubmit}
			className={cn(
				'flex w-full shrink-0 flex-col gap-m border-0 border-t border-solid border-grey-4 py-m',
				OSCAR_CONTENT_INSET,
				className
			)}
		>
			{prompt}
			<div className="flex w-full flex-col">
				<div className="flex w-full items-start gap-m">
					<Input.TextArea
						value={value}
						onChange={(event) => onChange(event.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={placeholder}
						aria-label={placeholder}
						// Grows with the answer instead of a single line — real questions run
						// to a paragraph. Enter sends, Shift+Enter breaks the line.
						autoSize={{ minRows: 1, maxRows: 8 }}
						// `!` is needed for the radius: antd is imported after
						// tailwind.out.css, so .ant-input's 2px would otherwise win. A pill
						// radius only reads right while it is a single line, so this uses the
						// panel's own radius once it wraps.
						className="bodyregular min-w-0 flex-1 !resize-none !rounded !px-s !py-[9px]"
					/>
					{sending && onCancel ? (
						<OWButton
							htmlType="button"
							size="large"
							shape="circle"
							icon={<StopOutlined style={{ fontSize: OSCAR_ICON_SIZE.send }} />}
							onClick={onCancel}
							aria-label="Stop"
						/>
					) : (
						<OWButton
							htmlType="submit"
							size="large"
							shape="circle"
							icon={<SendOutlined style={{ fontSize: OSCAR_ICON_SIZE.send }} />}
							loading={sending}
							disabled={disabled || !trimmed}
							aria-label="Send"
						/>
					)}
				</div>
				<span className="footnoteregular text-character-secondary">{OSCAR_DISCLAIMER_COPY}</span>
			</div>
		</form>
	);
};

export default OscarComposer;
